<script setup lang="ts">
import { computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useUxData } from "@/stores/ux-data";
import { useAppDataStore } from "@/stores/app-data";
import { usePredicateStore } from "@/stores/predicate-store";
import { useGuidedSetupStore } from "@/stores/guided-setup-store";
import { handleError } from "@/appdata/globals";
import GuidedStepper from '../GuidedStepper.vue';
import WorldNameStep from './steps/WorldNameStep.vue';
import WorldRulesStep from './steps/WorldRulesStep.vue';
import WorldSourcesStep from './steps/WorldSourcesStep.vue';
import type { WorldInterface } from "@/types/worlds";

const router = useRouter();
const ux = useUxData();
const store = useAppDataStore();
const predicateStore = usePredicateStore();
const setupStore = useGuidedSetupStore();

const steps = computed(() => [
    { label: ux.labels?.guidedSetup?.world?.steps?.name ?? 'Name' },
    { label: ux.labels?.guidedSetup?.world?.steps?.sources ?? 'Element Sources' },
    { label: ux.labels?.guidedSetup?.world?.steps?.rules ?? 'Setting Rules' },
]);

// Keep the store's step count in sync with the rendered steps (matches CharacterSetupFlow).
// Without this, a stale totalSteps (e.g. a persisted/hot-reloaded session) leaves the last step
// showing "Next" into a blank slot instead of "Create".
watch(steps, (newSteps) => {
    setupStore.setTotalSteps(newSteps.length);
}, { immediate: true });

const onSubmit = async () => {
    if (!setupStore.worldDraft) return;

    setupStore.setSubmitting(true);

    try {
        const world = await store.persistWorld(setupStore.worldDraft);
        // persistWorld refuses a blank name by resolving to undefined rather than throwing, so the
        // rest of this block would otherwise run on a refusal: completeFlow + resetFlow null the
        // draft and the router leaves the page, discarding the world the user just built. The store
        // has already queued the error notification; stay on the step so they can fix the name.
        if (!world) return;

        const selectedSources = setupStore.selectedSources;
        if (selectedSources.length > 0) {
            for (const entry of selectedSources) {
                predicateStore.elementFilters.types = entry.elementTypes;
                await store.associateElements(
                    world as WorldInterface,
                    entry.world,
                    predicateStore.associationPredicates
                );
            }
        }

        setupStore.completeFlow();
        setupStore.resetFlow();
        router.push({
          name: 'data-manager',
          params: {
            system: store.system?.alias ?? import.meta.env.VITE_SYSTEM_ALIAS,
            category: 'worlds',
          }
        });
    } catch (error) {
        handleError(error);
    } finally {
        setupStore.setSubmitting(false);
    }
};
</script>

<template>
    <GuidedStepper :steps="steps" @submit="onSubmit">
        <template #step-0>
            <WorldNameStep />
        </template>
        <template #step-1>
            <WorldSourcesStep />
        </template>
        <template #step-2>
            <WorldRulesStep />
        </template>
    </GuidedStepper>
</template>
